import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiPencil, HiTrash } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './PaymentMethodIndex.scss';

interface PaymentMethod {
  id: number;
  name: string;
  percent: number;
}

export const PaymentMethodIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['paymentMethods', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    });

    return params.toString();
  };

  const fetchPaymentMethods = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/paymentmethod?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('paymentMethods:messages.fetchError'));
        return;
      }

      setPaymentMethods(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error: any) {
      showErrorToast(t('paymentMethods:messages.fetchError'));
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods(currentPage, pageSize);
  }, [currentPage, pageSize, filters]);

  const deletePaymentMethod = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await adminApi.call(`/paymentmethod/${id}`, 'DELETE');

      if (!response.success) {
        showErrorToast(response.error || 'Error deleting payment method');
        return;
      }

      showSuccessToast('Item deleted successfully.');
      fetchPaymentMethods(currentPage, pageSize);
    } catch (error) {
      showErrorToast('Error: ' + (error as any).message);
      console.error('Error deleting payment method:', error);
    }
  };

  const handleCreatePaymentMethod = () => {
    navigate('/payment-methods/create');
  };

  const handleEditPaymentMethod = (id: number) => {
    navigate(`/payment-methods/edit/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: t('paymentMethods:table.name'),
      sortable: true,
      filterable: true,
      filterKey: 'name',
    },
    {
      key: 'percent',
      title: t('paymentMethods:fields.percent'),
      sortable: true,
    },
    {
      key: 'actions',
      title: t('paymentMethods:table.actions'),
      sortable: false,
      width: '150px',
      align: 'center',
      render: (_, method) => (
        <div className="action-buttons">
          {hasPermission("Update Payment Method") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditPaymentMethod(method.id)}
              tooltip={t('common:edit')}
            />
          )}
          {hasPermission("Delete Payment Method") && (
            <ActionButton
              variant="danger"
              size="sm"
              icon={HiTrash}
              onClick={() => deletePaymentMethod(method.id)}
              tooltip={t('common:delete')}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('paymentMethods:breadcrumbs.paymentMethods') }
  ];

  return (
    <PageLayout
      title={t('paymentMethods:title')}
      subtitle={t('paymentMethods:subtitle')}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={paymentMethods}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={false}
        refreshable={true}
        addButton={hasPermission("Create Payment Method") ? {
          label: t('paymentMethods:addNew'),
          onClick: handleCreatePaymentMethod
        } : undefined}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchPaymentMethods(currentPage, pageSize)}
        emptyMessage={t('paymentMethods:messages.noPaymentMethods')}
      />
    </PageLayout>
  );
};
